import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../state/app.state';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { FetchAllBidItemsRequest, FetchProjectDetailsRequest, FetchSiteDetailsRequest } from '../../../core/projects.actions';
import { allBidItems, dailyReport, projectDetails, siteDetails } from '../../../core/projects.selectors';
import { MapService } from '../../../../../shared/services/map.service';
import { DailyReportService } from '../../../../../shared/services/daily-report.service';
import { DailyReportCacheService } from '../../../../../shared/services/daily-report-cache.service';
import { BidService } from '../../../../../shared/services/bid.service';
import { MAP_VIEW_BY_COLORS, getLayerDefaults } from '../map.constants';
import { LabelByOption, MapLabelConfig } from '../../../../../shared/types/map.types';
import { StationRangeMapGeometryService } from '../../../../../shared/services/station-range-map-geometry.service';
import { LINKED_ONLY_FILTER } from '../../../../../shared/helpers/design-context';

export interface Inspector {
  id: string;
  name: string;
  email?: string;
  color?: string;
}

@Component({
    selector: 'app-daily-report-map-container',
    templateUrl: './daily-report-map-container.component.html',
    styleUrls: ['./daily-report-map-container.component.scss'],
    standalone: false
})
export class DailyReportMapContainerComponent implements OnInit, OnDestroy {
  @ViewChild('mapContainer', { static: true, read: ElementRef }) mapContainer: ElementRef;

  projectId: string;
  siteId: string;
  isFullScreen = false;
  fullscreenElement: HTMLElement;

  // Single day mode - always true for this component
  isSingleDayMode = true;

  // Date for single day view
  selectedDate: Date = new Date();
  dateRange = {
    start: null as Date,
    end: null as Date,
  };

  // Data for filtering
  private sites: any[] = [];
  private allProjectSites: any[] = [];
  filteredSites: any[] = [];
  private reportGeometries: any[] = [];
  private currentSitesWithGeometries: string[] = [];
  private siteColorMap: { [siteId: string]: string } = {};

  // Hover state for map fade effect
  hoveredSiteId: string | null = null;
  hoveredBidItemId: string | null = null;
  hoveredInspectorId: string | null = null;

  // Sticky selection state for multi-select highlighting
  selectedSiteIdsForHighlight: Set<string> = new Set();
  selectedBidItemIdsForHighlight: Set<string> = new Set();
  selectedInspectorIdsForHighlight: Set<string> = new Set();
  selectedDatesForHighlight: Set<string> = new Set();

  // View By state (site, bid_item, day, inspector)
  selectedViewBy: 'site' | 'bid_item' | 'day' | 'inspector' = 'inspector';

  // Bid items with geometries in current date range
  bidItemsWithGeometries: Array<{id: string, item: string, description: string}> = [];

  // Track whether we've successfully processed map data with valid siteDetails
  private hasProcessedMapDataWithSiteDetails = false;

  // Inspectors (Creators) for the current day's reports
  availableInspectors: Inspector[] = [];

  // Layer settings - use single day defaults (all layers enabled)
  layerSettings = getLayerDefaults('singleDay');

  // Type filter settings - all enabled by default in single day mode
  selectedTypeFilters: string[] = ['bidItem', 'activities', 'delivery', 'forms'];

  // Label state
  labelsEnabled = false;
  labelBy: LabelByOption = 'bid_item_name';
  labelConfig: MapLabelConfig = { enabled: false, labelBy: 'bid_item_name' };

  // Site filter settings
  availableSites: Array<{id: string, name: string}> = [];
  selectedSiteIds: string[] = [];

  // Bid item filter settings
  availableBidItems: Array<{id: string, item: string, description: string}> = [];
  selectedBidItemIds: string[] = [];

  // Picture source configuration
  private readonly PICTURE_SOURCES = {
    report_bid_item: { enabled: true, label: 'Bid Items' },
    report_activity: { enabled: true, label: 'Activities' },
    report_activity_code_header: { enabled: true, label: 'Activity Codes' },
    issue: { enabled: true, label: 'Issues' },
    report_material_delivery: { enabled: true, label: 'Material Deliveries' },
    report_internal_test: { enabled: true, label: 'Quality Tests' },
    report_labor: { enabled: true, label: 'Labor' },
    report_equipment: { enabled: true, label: 'Equipment' }
  };

  // Caching and prefetch
  cacheInfo: any = null;
  isLoadingFromAPI = false;
  projectDetails: any = null;
  siteDetails: any = null;
  private lastAlignmentRefreshKey: string | null = null;

  // Daily reports for the selected date
  dailyReports: any[] = [];

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private router: Router,
    private mapService: MapService,
    private dailyReportService: DailyReportService,
    private cacheService: DailyReportCacheService,
    private bidService: BidService,
    private stationRangeMapGeometry: StationRangeMapGeometryService
  ) {
    this.projectId = this.route.snapshot.paramMap.get('projectId');
    this.siteId = this.route.snapshot.paramMap.get('siteId');

    // Subscribe to query params to detect fullscreen mode and date changes
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        const wasFullScreen = this.isFullScreen;
        this.isFullScreen = 'fs' in qp;

        // Parse date from query params (single date mode)
        if (qp.date && moment(qp.date).isValid()) {
          const newDate = moment(qp.date).toDate();
          if (!this.selectedDate || this.selectedDate.getTime() !== newDate.getTime()) {
            this.selectedDate = newDate;
            // In single day mode, start and end are the same
            this.dateRange.start = newDate;
            this.dateRange.end = newDate;
            this.fetchDailyReportsForDate();
          }
        } else if (!qp.date) {
          // Default to today if no date specified
          const today = new Date();
          this.selectedDate = today;
          this.dateRange.start = today;
          this.dateRange.end = today;
          // Update URL with today's date
          this.router.navigate([], {
            queryParams: { date: moment(today).format('YYYY-MM-DD') },
            queryParamsHandling: 'merge',
          });
        }

        // If we just entered fullscreen, don't re-process data
        // Data is already loaded in non-fullscreen mode, re-processing would trigger re-zoom
        // The map component handles fullscreen transition without needing data refresh

        // If we just exited fullscreen, reset
        if (!this.isFullScreen && wasFullScreen) {
          this.resetLayerSettings();
        }
      });
  }

  ngOnInit() {
    // Get reference to the map container element for fullscreen
    this.fullscreenElement = this.mapContainer.nativeElement;

    // Fetch project details
    this.store.dispatch(FetchProjectDetailsRequest({
      payload: {
        projectId: this.projectId,
        qp: {
          include: [
            'online',
            {
              sites: ['geometry', 'delivery_tracking_fields', { headings: ['stations'] }],
            },
            {
              bid_items: ['rollup_fields', 'materials', { fields: ['recent_fields'] }],
            },
          ],
        },
      },
    }));

    // Fetch site details (includes geometry) - needed for single day mode
    this.store.dispatch(FetchSiteDetailsRequest({
      payload: {
        projectId: this.projectId,
        siteId: this.siteId,
        qp: {
          include: ['geometry'],
        },
      },
    }));

    // Subscribe to project details
    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (Object.keys(data).length !== 0) {
          this.projectDetails = data;
          this.allProjectSites = data.sites || [];

          // Populate available sites (only the current site for single day view)
          if (data.sites && data.sites.length > 0) {
            const currentSite = data.sites.find(s => s.id === this.siteId);
            if (currentSite) {
              this.availableSites = [{
                id: currentSite.id,
                name: currentSite.name
              }];
              this.siteDetails = currentSite;
            }
          }

          // Fetch all bid items for the project
          if (data.id) {
            this.store.dispatch(FetchAllBidItemsRequest({
              payload: {
                projectId: data.id,
                qp: {
                  include: [{ bid_items: ['fields'] }],
                },
              }
            }));
          }

          this.refreshLocationDerivedDataAfterAlignmentLoad();
        }
      });

    // Subscribe to site details
    this.store.select(siteDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(site => {
        // Only process if this is the site we're viewing (prevents stale data from other sites)
        if (site && site.id === this.siteId) {
          this.siteDetails = site;

          // If we have daily reports but haven't processed map data with valid siteDetails yet, do it now
          // This handles the race condition where daily reports returned before siteDetails was populated
          if (!this.hasProcessedMapDataWithSiteDetails && this.dailyReports?.length > 0) {
            // Re-build filtered sites (needed for markers) and re-fetch geometries
            this.buildFilteredSites();
            this.fetchReportGeometries();
            this.hasProcessedMapDataWithSiteDetails = true;
          }
        }
      });

    // Subscribe to all bid items for filter dropdown
    this.store.select(allBidItems)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(bidItems => {
        if (bidItems && bidItems.length > 0) {
          this.availableBidItems = bidItems.map(bidItem => ({
            id: bidItem.id,
            item: bidItem.item,
            description: bidItem.description
          }));
        } else {
          this.availableBidItems = [];
        }
      });

    // Subscribe to dailyReport selector to react to changes (bid item add/edit/delete)
    // This enables real-time map updates when the user modifies bid items in the daily report
    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(report => {
        // Only process if report exists and matches current site
        if (report && report.id && report.site_id === this.siteId) {
          // Check if report date matches selected date
          const reportDate = report.date || report.report_date;
          const selectedDateStr = moment(this.selectedDate).format('YYYY-MM-DD');

          if (reportDate === selectedDateStr) {
            // Update dailyReports array with the updated report data
            // Create a new array to ensure change detection
            const existingIndex = this.dailyReports.findIndex(r => r.id === report.id);
            if (existingIndex >= 0) {
              // Replace existing report with updated data
              this.dailyReports = [
                ...this.dailyReports.slice(0, existingIndex),
                report,
                ...this.dailyReports.slice(existingIndex + 1)
              ];
            } else if (this.dailyReports.length === 0) {
              // First report for this date
              this.dailyReports = [report];
            }

            // Re-process markers to update the map (including no-location badge)
            this.fetchBidItemMarkers();
            this.fetchActivityMarkers();
            this.fetchDeliveryMarkers();
            this.fetchFormMarkers();
            this.fetchPictureMarkers();
            this.fetchReportGeometries();
          }
        }
      });

    // Initial fetch of daily reports
    this.fetchDailyReportsForDate();
  }

  onFullscreenChange(isFullscreen: boolean): void {
    this.isFullScreen = isFullscreen;
  }

  onSearchChange(query: string): void {
    // TODO: Implement search functionality
  }

  onDateChange(date: Date): void {
    this.selectedDate = date;
    this.dateRange.start = date;
    this.dateRange.end = date;

    // Update URL params
    this.router.navigate([], {
      queryParams: { date: moment(date).format('YYYY-MM-DD') },
      queryParamsHandling: 'merge',
    });

    // Fetch reports for new date
    this.fetchDailyReportsForDate();
  }

  onViewModeChange(mode: string): void {
    // TODO: Implement view mode switching
  }

  onSiteHover(siteId: string | null): void {
    this.hoveredSiteId = siteId;
  }

  onBidItemHover(bidItemId: string | null): void {
    this.hoveredBidItemId = bidItemId;
  }

  onInspectorHover(inspectorId: string | null): void {
    this.hoveredInspectorId = inspectorId;
  }

  onViewByChange(viewBy: 'site' | 'bid_item' | 'day' | 'inspector'): void {
    this.selectedViewBy = viewBy;
  }

  // Sticky selection event handlers (multi-select)
  onSiteSelectionChange(selectedIds: Set<string>): void {
    this.selectedSiteIdsForHighlight = selectedIds;
  }

  onBidItemSelectionChange(selectedIds: Set<string>): void {
    this.selectedBidItemIdsForHighlight = selectedIds;
  }

  onInspectorSelectionChange(selectedIds: Set<string>): void {
    this.selectedInspectorIdsForHighlight = selectedIds;
  }

  onDateSelectionChange(selectedDates: Set<string>): void {
    this.selectedDatesForHighlight = selectedDates;
  }

  onLayerChange(layers: any): void {
    this.layerSettings = { ...layers };
    this.updateMapWithGeometries();

    // Fetch picture markers if picture layer changed
    if (layers.picture !== undefined) {
      this.fetchPictureMarkers();
    }
  }

  onLabelToggle(): void {
    this.labelsEnabled = !this.labelsEnabled;
    this.labelConfig = { enabled: this.labelsEnabled, labelBy: this.labelBy };
  }

  onLabelByChange(labelBy: LabelByOption): void {
    this.labelBy = labelBy;
    this.labelConfig = { enabled: this.labelsEnabled, labelBy: this.labelBy };
  }

  /** Spec §7.3 map filter: the Types tab's `linked_only` token, drawn by sites-location-map. */
  get linkedOnly(): boolean {
    return this.selectedTypeFilters.includes(LINKED_ONLY_FILTER);
  }

  // 'linked_only' is a display filter handled inside sites-location-map (bound through
  // `linkedOnly`); it passes through here untouched and never counts as a type or form group.
  onTypeFilterChange(typeFilters: string[]): void {
    this.selectedTypeFilters = typeFilters;

    // Update layerSettings to sync with type filters
    this.layerSettings.bidItem = typeFilters.includes('bidItem');
    this.layerSettings.activity = typeFilters.includes('activities');
    this.layerSettings.delivery = typeFilters.includes('delivery');
    this.layerSettings.forms = typeFilters.includes('forms');

    // Emit layer settings update
    // In daily report mode, show all layers including as-builts in non-fullscreen mode
    this.mapService.layerSettingsUpdated.next({
      ...this.layerSettings
    });

    // Fetch markers based on selected type filters
    this.fetchBidItemMarkers();
    this.fetchActivityMarkers();
    this.fetchDeliveryMarkers();
    this.fetchFormMarkers();
  }

  onBidItemFilterChange(selectedBidItemIds: string[]): void {
    this.selectedBidItemIds = selectedBidItemIds;
    this.mapService.selectedBidItemIds.next(selectedBidItemIds);
  }

  private fetchDailyReportsForDate(): void {
    if (!this.projectId || !this.siteId || !this.selectedDate) {
      return;
    }

    const dateStr = moment(this.selectedDate).format('YYYY-MM-DD');

    // Fetch daily reports for this site and date
    this.dailyReportService.getBulkDailyReports(this.projectId, dateStr, dateStr, {
      include: [
        'site',
        'createdBy',
        {
          bid_items: [
            'map_feature_link',
            'sources',
            { bid_item: ['fields'] },
            'heading',
            'station',
            'pictures',
            'field_values'
          ],
        },
        {
          activities: ['map_feature_link', 'pictures']
        },
        {
          material_deliveries: [
            'map_feature_link',
            'pictures',
            'tickets',
            'bid_item',
            'material',
            { annotations: ['drawing'] },
            'field_values'
          ]
        },
        {
          control_forms: ['map_feature_link', 'control_form', 'pictures', 'lines']
        },
      ],
      useCache: true
    })
      .pipe(takeUntil(this.onDestroy))
      .subscribe(
        (reports: any[]) => {
          // Filter reports to only include those from the current site
          this.dailyReports = reports.filter(report => {
            const reportSiteId = report.site_id || report.site?.id;
            return reportSiteId === this.siteId;
          });

          // Process the report data
          this.processReportData();
        },
        error => {
          console.error('Error fetching daily reports:', error);
          this.dailyReports = [];
        }
      );
  }

  private processReportData(): void {
    // Extract unique inspectors (creators) from reports
    this.extractInspectors();

    // Extract bid items present in reports
    this.extractBidItemsFromReports();

    // Build filtered sites data
    this.buildFilteredSites();

    // Fetch report geometries
    this.fetchReportGeometries();

    // Fetch markers - in single day mode, all layers are ON by default
    // Always fetch pictures in single day mode
    this.fetchPictureMarkers();

    // Always fetch all marker types in single day mode
    this.fetchBidItemMarkers();
    this.fetchActivityMarkers();
    this.fetchDeliveryMarkers();
    this.fetchFormMarkers();
  }

  private extractInspectors(): void {
    const inspectorMap = new Map<string, Inspector>();
    const colorPalette = MAP_VIEW_BY_COLORS;

    this.dailyReports.forEach(report => {
      // Check if createdBy is a fully expanded object with id property (API returns camelCase)
      const creator = report.createdBy || report.created_by;
      if (creator && typeof creator === 'object' && creator.id) {
        if (!inspectorMap.has(creator.id)) {
          const index = inspectorMap.size;
          inspectorMap.set(creator.id, {
            id: creator.id,
            name: `${creator.first_name || ''} ${creator.last_name || ''}`.trim() || creator.email || 'Unknown',
            email: creator.email,
            color: colorPalette[index % colorPalette.length]
          });
        }
      } else {
        // Fallback: createdBy might be just an ID (number/string) or use created_by_id
        const inspectorId = report.created_by_id || creator;
        if (inspectorId && !inspectorMap.has(String(inspectorId))) {
          const index = inspectorMap.size;
          inspectorMap.set(String(inspectorId), {
            id: String(inspectorId),
            name: `Inspector ${index + 1}`,
            color: colorPalette[index % colorPalette.length]
          });
        }
      }
    });

    this.availableInspectors = Array.from(inspectorMap.values());
  }

  private extractBidItemsFromReports(): void {
    const bidItemMap = new Map<string, {id: string, item: string, description: string}>();

    this.dailyReports.forEach(report => {
      if (report.bid_items && report.bid_items.length > 0) {
        report.bid_items.forEach(reportBidItem => {
          const bidItem = reportBidItem.bid_item;
          if (bidItem && bidItem.id && !bidItemMap.has(bidItem.id)) {
            bidItemMap.set(bidItem.id, {
              id: bidItem.id,
              item: bidItem.item || '',
              description: bidItem.description || ''
            });
          }
        });
      }

    });

    this.bidItemsWithGeometries = Array.from(bidItemMap.values())
      .sort((a, b) => a.item.localeCompare(b.item));
  }

  private buildFilteredSites(): void {
    // For single day mode, we only have one site
    if (this.siteDetails) {
      this.filteredSites = [{
        site_id: this.siteDetails.id,
        site_name: this.siteDetails.name,
        longitude: this.siteDetails.longitude,
        latitude: this.siteDetails.latitude,
        daily_reports: this.dailyReports,
        isFiltered: true
      }];
    }

    // Send to map service
    const enhancedSites = this.filteredSites.map(site => ({
      ...site,
      isFiltered: true
    }));

    this.mapService.initiateSiteLocationsMap.next(enhancedSites);
  }

  private fetchReportGeometries(): void {
    // Guard: Don't emit if siteDetails is not loaded for the current site
    // This prevents overwriting correct geometry state with incomplete data
    if (!this.siteDetails || this.siteDetails.id !== this.siteId) {
      return;
    }

    // Mark that we've successfully processed map data with valid siteDetails
    this.hasProcessedMapDataWithSiteDetails = true;

    // Clear existing geometries
    this.reportGeometries = [];

    // Add site geometry if available
    if (this.siteDetails.geometry) {
      this.reportGeometries.push({
        geometry: this.siteDetails.geometry,
        type: 'site',
        reportId: null,
        siteId: this.siteDetails.id,
      });
    }

    // Extract bid item geometries from reports
    this.dailyReports.forEach(report => {
      const creatorId = report.created_by_id || report.createdBy?.id || report.created_by?.id;
      if (report.bid_items) {
        report.bid_items.forEach(bidItem => {
          // Extract geometry directly from report_bid_item.geometry (GPS devices)
          if (bidItem.geometry) {
            BidService.extractGeometries(bidItem.geometry).forEach(extracted => {
              this.reportGeometries.push({
                geometry: extracted.geometry,
                bidItem: bidItem.bid_item,
                report_bid_item_id: bidItem.id || null,
                bid_item_id: bidItem.bid_item?.id || null,
                reportId: report.id,
                siteId: report.site_id || report.site?.id,
                report_date: report.date,
                created_by_id: creatorId,
                ...(extracted.gps_feature_index != null ? { gps_feature_index: extracted.gps_feature_index } : {}),
              });
            });
          }

          // Extract from quantity sources (existing flow)
          else if (bidItem.sources && bidItem.sources.length) {
            bidItem.sources.forEach(source => {
              if (source.source_geometry) {
                this.reportGeometries.push({
                  geometry: source.source_geometry,
                  bidItem: bidItem.bid_item,
                  report_bid_item_id: bidItem.id || null,
                  bid_item_id: bidItem.bid_item?.id || null,
                  reportId: report.id,
                  siteId: report.site_id || report.site?.id,
                  report_date: report.date,
                  created_by_id: creatorId,
                });
              }
            });
          }

          // Station ranges are NOT part of the geometry pipeline — they render on
          // a dedicated, drilldown-only station-range layer in sites-location-map.
        });
      }
    });

    // Update map with geometries
    this.updateMapWithGeometries();
  }

  private updateMapWithGeometries(): void {
    // Inspector color palette - ensure string keys for consistent matching
    const inspectorColorMap: { [inspectorId: string]: string } = {};
    this.availableInspectors.forEach(inspector => {
      inspectorColorMap[String(inspector.id)] = inspector.color;
    });

    // Build unique geometries
    const uniqueGeometries = [];
    const sitesWithGeometries = new Set<string>();

    this.reportGeometries.forEach(item => {
      if (item.type === 'site' && item.siteId) {
        sitesWithGeometries.add(item.siteId);
      }

      // Two bid items legitimately share a trace (sawcut / removal / new concrete
      // along one curb), so identity has to be part of the key — otherwise the
      // later item's geometry is dropped and its clicks resolve to the earlier one.
      const exists = uniqueGeometries.some(existing =>
        (existing.report_bid_item_id || null) === (item.report_bid_item_id || null) &&
        JSON.stringify(existing.coordinates) === JSON.stringify(item.geometry.coordinates) &&
        existing.type === item.geometry.type
      );

      if (!exists) {
        // Determine color based on inspector - ensure string comparison for ID matching
        const creatorId = item.created_by_id ? String(item.created_by_id) : null;
        const inspectorColor = creatorId ? inspectorColorMap[creatorId] : null;
        // Default to blue if no inspector color found (not grey)
        const geometryColor = item.geometry.color || inspectorColor || '#0091FF';

        uniqueGeometries.push({
          ...item.geometry,
          siteId: item.siteId,
          isSiteGeometry: item.type === 'site',
          report_bid_item_id: item.report_bid_item_id || null,
          bid_item_id: item.bid_item_id || null,
          bidItemId: item.bid_item_id || null,
          report_activity_id: item.report_activity_id || null,
          report_material_delivery_id: item.report_material_delivery_id || null,
          created_by_id: creatorId,
          gps_feature_index: item.gps_feature_index !== undefined ? item.gps_feature_index : null,
          color: geometryColor,
          lineStyle: item.geometry.lineStyle || null,
          strokeWidth: item.geometry.strokeWidth || null,
          opacity: item.geometry.opacity ?? item.geometry.lineOpacity ?? null,
          bid_item_name: item.bidItem?.description || item.bidItem?.item || '',
          bid_item_code: item.bidItem?.item || '',
          bid_item_unit: item.bidItem?.unit || '',
          quantity: item.quantity || 0,
          site_name: this.siteDetails?.name || ''
        });
      }
    });

    this.currentSitesWithGeometries = Array.from(sitesWithGeometries);

    // Send geometries to the map component
    // In daily report mode, show all layers including as-builts in non-fullscreen mode
    this.mapService.siteReportGeometries.next({
      geometries: uniqueGeometries,
      sitesWithGeometries: this.currentSitesWithGeometries,
      siteColorMap: this.siteColorMap,
      inspectorColorMap: inspectorColorMap,
      layerSettings: this.layerSettings
    });
  }

  private fetchPictureMarkers(): void {
    if (!this.layerSettings.picture) {
      this.mapService.dailyReportPictures.next([]);
      return;
    }

    const dateStr = moment(this.selectedDate).format('YYYY-MM-DD');
    const siteIds = [this.siteId];
    const enabledSources = Object.keys(this.PICTURE_SOURCES).filter(
      key => this.PICTURE_SOURCES[key].enabled
    );

    // Build a mapping from report_uuid to created_by_id for enriching pictures
    const reportToCreatorMap = new Map<string, string>();
    this.dailyReports.forEach(report => {
      const creatorId = report.created_by_id || report.createdBy?.id || report.created_by?.id;
      if (report.id && creatorId) {
        reportToCreatorMap.set(report.id, creatorId);
      }
    });

    this.dailyReportService.getPictures(
      this.projectId,
      dateStr,
      dateStr,
      {
        siteIds: siteIds,
        sources: enabledSources,
        limit: 1000,
        useCache: true
      }
    )
      .pipe(takeUntil(this.onDestroy))
      .subscribe(
        (response) => {
          // Enrich pictures with created_by_id from their associated report
          const enrichedPictures = response.data.map(picture => {
            const creatorId = picture.report_uuid ? reportToCreatorMap.get(picture.report_uuid) : null;
            return {
              ...picture,
              created_by_id: creatorId || null
            };
          });
          this.mapService.dailyReportPictures.next(enrichedPictures);
        },
        (error) => {
          this.mapService.dailyReportPictures.next([]);
        }
      );
  }

  private fetchBidItemMarkers(): void {
    const showBidItems = this.selectedTypeFilters.includes('bidItem');

    if (!showBidItems) {
      this.mapService.dailyReportBidItems.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      return;
    }

    const bidItemMarkers = [];
    const noLocationBidItems = [];
    const noLocationUniqueBidItems = new Set<string>();

    // Group bid items by report + bid_item
    const bidItemGroups = new Map<string, {
      reportId: string;
      siteId: string;
      siteName: string;
      reportDate: string;
      projectId: string;
      bidItem: any;
      lineItems: any[];
      locations: { latitude: number; longitude: number }[];
      totalQuantity: number;
      createdById: string;
    }>();

    this.dailyReports.forEach(report => {
      if (!report.bid_items || !report.bid_items.length) {
        return;
      }

      report.bid_items.forEach(bidItem => {
        const location = this.calculateBidItemLocation(bidItem, report);
        const uniqueKey = `${report.id}:${bidItem.bid_item?.id}`;

        if (location) {
          if (!bidItemGroups.has(uniqueKey)) {
            bidItemGroups.set(uniqueKey, {
              reportId: report.id,
              siteId: report.site_id,
              siteName: report.site?.name,
              reportDate: report.date || report.report_date,
              projectId: report.project_id,
              bidItem: bidItem.bid_item,
              lineItems: [],
              locations: [],
              totalQuantity: 0,
              createdById: report.created_by_id || report.createdBy?.id || report.created_by?.id
            });
          }

          const group = bidItemGroups.get(uniqueKey);
          group.lineItems.push(bidItem);
          group.locations.push(location);

          // Calculate quantity
          const transformedLineItem = this.transformSourceFieldsToFieldValues(bidItem);
          const lineItemQty = this.bidService.calculateBidItemTotalQuantity(bidItem.bid_item, [transformedLineItem]);
          group.totalQuantity += lineItemQty;
        } else {
          if (!noLocationUniqueBidItems.has(uniqueKey)) {
            noLocationUniqueBidItems.add(uniqueKey);
            noLocationBidItems.push({
              reportId: report.id,
              siteId: report.site_id,
              siteName: report.site?.name,
              reportDate: report.date || report.report_date,
              projectId: report.project_id,
              bidItem: bidItem.bid_item,
              reportBidItem: bidItem,
              comment: bidItem.comment,
              allLineItems: [bidItem],
              createdById: report.created_by_id || report.createdBy?.id || report.created_by?.id
            });
          } else {
            const existingItem = noLocationBidItems.find(item =>
              item.bidItem?.id === bidItem.bid_item?.id && item.reportId === report.id
            );
            if (existingItem && existingItem.allLineItems) {
              existingItem.allLineItems.push(bidItem);
            }
          }
        }
      });
    });

    // Create markers from groups
    bidItemGroups.forEach((group, key) => {
      const totalLat = group.locations.reduce((sum, loc) => sum + loc.latitude, 0);
      const totalLon = group.locations.reduce((sum, loc) => sum + loc.longitude, 0);
      const centerPoint = {
        latitude: totalLat / group.locations.length,
        longitude: totalLon / group.locations.length
      };

      const allChildren = group.lineItems.reduce((acc, lineItem) => {
        if (lineItem.children && lineItem.children.length > 0) {
          return [...acc, ...lineItem.children];
        }
        return acc;
      }, []);

      const allSources = group.lineItems.reduce((acc, lineItem) => {
        if (lineItem.sources && lineItem.sources.length > 0) {
          return [...acc, ...lineItem.sources];
        }
        return acc;
      }, []);

      const markerData = {
        ...group.lineItems[0],
        quantity: group.totalQuantity,
        children: allChildren,
        sources: allSources,
        reportId: group.reportId,
        siteId: group.siteId,
        siteName: group.siteName,
        reportDate: group.reportDate,
        projectId: group.projectId,
        level: 1,
        bid_item: group.bidItem,
        allLineItems: group.lineItems,
        created_by_id: group.createdById,
      };

      bidItemMarkers.push({
        geometry: {
          type: 'point',
          longitude: centerPoint.longitude,
          latitude: centerPoint.latitude,
        },
        symbol: 'bid_items',
        data: markerData,
        category: 'bid_items'
      });
    });

    this.mapService.dailyReportBidItems.next({
      markers: bidItemMarkers,
      noLocationCount: noLocationUniqueBidItems.size,
      noLocationItems: noLocationBidItems
    });
  }

  private fetchActivityMarkers(): void {
    const showActivities = this.selectedTypeFilters.includes('activities');

    if (!showActivities) {
      this.mapService.dailyReportActivities.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      return;
    }

    const activityMarkers = [];
    const noLocationActivities = [];
    const noLocationUniqueActivities = new Set<string>();

    const activityGroups = new Map<string, {
      reportId: string;
      siteId: string;
      siteName: string;
      reportDate: string;
      projectId: string;
      activity: any;
      locations: { latitude: number; longitude: number }[];
      pictures: any[];
      createdById: string;
    }>();

    this.dailyReports.forEach(report => {
      if (!report.activities || !report.activities.length) {
        return;
      }

      report.activities.forEach(activity => {
        const location = this.calculateActivityLocation(activity, report);
        const uniqueKey = `${report.id}:${activity.id}`;

        if (location) {
          if (!activityGroups.has(uniqueKey)) {
            activityGroups.set(uniqueKey, {
              reportId: report.id,
              siteId: report.site_id,
              siteName: report.site?.name,
              reportDate: report.date || report.report_date,
              projectId: report.project_id,
              activity: activity,
              locations: [],
              pictures: [],
              createdById: report.created_by_id || report.createdBy?.id || report.created_by?.id
            });
          }

          const group = activityGroups.get(uniqueKey);
          group.locations.push(location);
          if (activity.pictures && activity.pictures.length > 0) {
            group.pictures.push(...activity.pictures);
          }
        } else {
          if (!noLocationUniqueActivities.has(uniqueKey)) {
            noLocationUniqueActivities.add(uniqueKey);
            noLocationActivities.push({
              reportId: report.id,
              siteId: report.site_id,
              siteName: report.site?.name,
              reportDate: report.date || report.report_date,
              projectId: report.project_id,
              activity: activity,
              comment: activity.comment,
              pictures: activity.pictures || [],
              createdById: report.created_by_id || report.createdBy?.id || report.created_by?.id
            });
          }
        }
      });
    });

    // Create markers
    activityGroups.forEach((group, key) => {
      const totalLat = group.locations.reduce((sum, loc) => sum + loc.latitude, 0);
      const totalLon = group.locations.reduce((sum, loc) => sum + loc.longitude, 0);
      const centerPoint = {
        latitude: totalLat / group.locations.length,
        longitude: totalLon / group.locations.length
      };

      const markerData = {
        ...group.activity,
        reportId: group.reportId,
        siteId: group.siteId,
        siteName: group.siteName,
        reportDate: group.reportDate,
        projectId: group.projectId,
        level: 1,
        pictures: group.pictures,
        title: group.activity.title || group.activity.name,
        annotations: [],
        created_by_id: group.createdById,
      };

      activityMarkers.push({
        geometry: {
          type: 'point',
          longitude: centerPoint.longitude,
          latitude: centerPoint.latitude,
        },
        symbol: 'activities',
        data: markerData,
        category: 'activities'
      });
    });

    this.mapService.dailyReportActivities.next({
      markers: activityMarkers,
      noLocationCount: noLocationUniqueActivities.size,
      noLocationItems: noLocationActivities
    });
  }

  private fetchDeliveryMarkers(): void {
    const showDeliveries = this.selectedTypeFilters.includes('delivery');

    if (!showDeliveries) {
      this.mapService.dailyReportDeliveries.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      return;
    }

    const deliveryMarkers = [];
    const noLocationDeliveries = [];
    const noLocationUniqueDeliveries = new Set<string>();

    const deliveryGroups = new Map<string, {
      reportId: string;
      siteId: string;
      siteName: string;
      reportDate: string;
      projectId: string;
      delivery: any;
      locations: { latitude: number; longitude: number }[];
      pictures: any[];
      createdById: string;
    }>();

    this.dailyReports.forEach(report => {
      if (!report.material_deliveries || !report.material_deliveries.length) {
        return;
      }

      report.material_deliveries.forEach(delivery => {
        const location = this.calculateDeliveryLocation(delivery, report);
        const uniqueKey = `${report.id}:${delivery.id}`;

        if (location) {
          if (!deliveryGroups.has(uniqueKey)) {
            deliveryGroups.set(uniqueKey, {
              reportId: report.id,
              siteId: report.site_id,
              siteName: report.site?.name,
              reportDate: report.date || report.report_date,
              projectId: report.project_id,
              delivery: delivery,
              locations: [],
              pictures: [],
              createdById: report.created_by_id || report.createdBy?.id || report.created_by?.id
            });
          }

          const group = deliveryGroups.get(uniqueKey);
          group.locations.push(location);
          if (delivery.pictures && delivery.pictures.length > 0) {
            group.pictures.push(...delivery.pictures);
          }
        } else {
          if (!noLocationUniqueDeliveries.has(uniqueKey)) {
            noLocationUniqueDeliveries.add(uniqueKey);
            noLocationDeliveries.push({
              ...delivery,
              reportId: report.id,
              siteId: report.site_id,
              siteName: report.site?.name,
              reportDate: report.date || report.report_date,
              projectId: report.project_id,
              pictures: delivery.pictures || [],
              createdById: report.created_by_id || report.createdBy?.id || report.created_by?.id
            });
          }
        }
      });
    });

    // Create markers
    deliveryGroups.forEach((group, key) => {
      const totalLat = group.locations.reduce((sum, loc) => sum + loc.latitude, 0);
      const totalLon = group.locations.reduce((sum, loc) => sum + loc.longitude, 0);
      const centerPoint = {
        latitude: totalLat / group.locations.length,
        longitude: totalLon / group.locations.length
      };

      const markerData = {
        ...group.delivery,
        reportId: group.reportId,
        siteId: group.siteId,
        siteName: group.siteName,
        reportDate: group.reportDate,
        projectId: group.projectId,
        level: 1,
        pictures: group.pictures,
        title: group.delivery.title || group.delivery.name || 'Material Delivery',
        created_by_id: group.createdById,
      };

      deliveryMarkers.push({
        geometry: {
          type: 'point',
          longitude: centerPoint.longitude,
          latitude: centerPoint.latitude,
        },
        symbol: 'deliveries',
        data: markerData,
        category: 'deliveries'
      });
    });

    this.mapService.dailyReportDeliveries.next({
      markers: deliveryMarkers,
      noLocationCount: noLocationUniqueDeliveries.size,
      noLocationItems: noLocationDeliveries
    });
  }

  private fetchFormMarkers(): void {
    const showForms = this.selectedTypeFilters.includes('forms');

    if (!showForms) {
      this.mapService.dailyReportForms.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      return;
    }

    const formMarkers = [];
    const noLocationForms = [];
    const noLocationUniqueForms = new Set<string>();

    const formGroups = new Map<string, {
      reportId: string;
      siteId: string;
      siteName: string;
      reportDate: string;
      projectId: string;
      form: any;
      locations: { latitude: number; longitude: number }[];
      pictures: any[];
      createdById: string;
    }>();

    this.dailyReports.forEach(report => {
      if (!report.control_forms || !report.control_forms.length) {
        return;
      }

      report.control_forms.forEach(form => {
        const location = this.calculateControlFormLocation(form, report);
        const uniqueKey = `${report.id}:${form.id}`;

        if (location) {
          if (!formGroups.has(uniqueKey)) {
            formGroups.set(uniqueKey, {
              reportId: report.id,
              siteId: report.site_id,
              siteName: report.site?.name,
              reportDate: report.date || report.report_date,
              projectId: report.project_id,
              form: form,
              locations: [],
              pictures: [],
              createdById: report.created_by_id || report.createdBy?.id || report.created_by?.id
            });
          }

          const group = formGroups.get(uniqueKey);
          group.locations.push(location);
          if (form.pictures && form.pictures.length > 0) {
            group.pictures.push(...form.pictures);
          }
        } else {
          if (!noLocationUniqueForms.has(uniqueKey)) {
            noLocationUniqueForms.add(uniqueKey);
            noLocationForms.push({
              ...form,
              control_form: form.control_form,
              reportId: report.id,
              siteId: report.site_id,
              siteName: report.site?.name,
              reportDate: report.date || report.report_date,
              projectId: report.project_id,
              pictures: form.pictures || [],
              createdById: report.created_by_id || report.createdBy?.id || report.created_by?.id
            });
          }
        }
      });
    });

    // Create markers
    formGroups.forEach((group, key) => {
      const totalLat = group.locations.reduce((sum, loc) => sum + loc.latitude, 0);
      const totalLon = group.locations.reduce((sum, loc) => sum + loc.longitude, 0);
      const centerPoint = {
        latitude: totalLat / group.locations.length,
        longitude: totalLon / group.locations.length
      };

      const markerData = {
        ...group.form,
        control_form: group.form.control_form,
        reportId: group.reportId,
        siteId: group.siteId,
        siteName: group.siteName,
        reportDate: group.reportDate,
        projectId: group.projectId,
        level: 1,
        pictures: group.pictures,
        title: group.form.control_form?.name,
        created_by_id: group.createdById,
        _mapItemType: 'forms',
      };

      formMarkers.push({
        geometry: {
          type: 'point',
          longitude: centerPoint.longitude,
          latitude: centerPoint.latitude,
        },
        symbol: 'forms',
        data: markerData,
        category: 'forms'
      });
    });

    this.mapService.dailyReportForms.next({
      markers: formMarkers,
      noLocationCount: noLocationUniqueForms.size,
      noLocationItems: noLocationForms
    });
  }

  private calculateBidItemLocation(bidItem: any, report: any): { latitude: number, longitude: number } | null {
    const stationRangeLocation = this.getStationRangeLocation(bidItem, report);
    if (stationRangeLocation) {
      return stationRangeLocation;
    }

    // Priority 1: Direct geometry (GPS devices)
    if (bidItem.geometry) {
      const coordinates = BidService.extractCoordinates(bidItem.geometry);
      if (coordinates.length > 0) {
        const centerPoint = this.calculateGeometryCenterPoint(coordinates);
        if (centerPoint) return centerPoint;
      }
    }

    // Priority 2: As-builts from sources
    if (bidItem.sources && bidItem.sources.length > 0) {
      const geometriesWithCoords = bidItem.sources
        .filter(source => source.source_geometry && source.source_geometry.coordinates)
        .map(source => source.source_geometry.coordinates);

      if (geometriesWithCoords.length > 0) {
        const centerPoint = this.calculateGeometryCenterPoint(geometriesWithCoords);
        if (centerPoint) {
          return centerPoint;
        }
      }
    }

    // Priority 2: Lat/long on bid item
    if (bidItem.latitude && bidItem.longitude) {
      return {
        latitude: parseFloat(bidItem.latitude),
        longitude: parseFloat(bidItem.longitude)
      };
    }

    // Priority 3: Pictures with locations
    if (bidItem.pictures && bidItem.pictures.length > 0) {
      const picturesWithLocation = bidItem.pictures.filter(pic => pic.latitude && pic.longitude);
      if (picturesWithLocation.length > 0) {
        return this.calculatePicturesCenterPoint(picturesWithLocation);
      }
    }

    return null;
  }

  private calculateActivityLocation(activity: any, report: any): { latitude: number, longitude: number } | null {
    const stationRangeLocation = this.getStationRangeLocation(activity, report);
    if (stationRangeLocation) {
      return stationRangeLocation;
    }

    // Priority 1: Lat/long on activity
    if (activity.latitude && activity.longitude) {
      return {
        latitude: parseFloat(activity.latitude),
        longitude: parseFloat(activity.longitude)
      };
    }

    // Priority 2: Pictures with locations
    if (activity.pictures && activity.pictures.length > 0) {
      const picturesWithLocation = activity.pictures.filter(pic => pic.latitude && pic.longitude);
      if (picturesWithLocation.length > 0) {
        return this.calculatePicturesCenterPoint(picturesWithLocation);
      }
    }

    return null;
  }

  private calculateDeliveryLocation(delivery: any, report: any): { latitude: number, longitude: number } | null {
    const stationRangeLocation = this.getStationRangeLocation(delivery, report);
    if (stationRangeLocation) {
      return stationRangeLocation;
    }

    // Priority 1: Lat/long on delivery
    if (delivery.latitude && delivery.longitude) {
      return {
        latitude: parseFloat(delivery.latitude),
        longitude: parseFloat(delivery.longitude)
      };
    }

    // Priority 2: Pictures with locations
    if (delivery.pictures && delivery.pictures.length > 0) {
      const picturesWithLocation = delivery.pictures.filter(pic => pic.latitude && pic.longitude);
      if (picturesWithLocation.length > 0) {
        return this.calculatePicturesCenterPoint(picturesWithLocation);
      }
    }

    return null;
  }

  private calculateControlFormLocation(form: any, report: any): { latitude: number, longitude: number } | null {
    const stationRangeLocation = this.getStationRangeLocation(form, report);
    if (stationRangeLocation) {
      return stationRangeLocation;
    }

    // Priority 1: Lat/long on form
    if (form.latitude && form.longitude) {
      return {
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude)
      };
    }

    // Priority 2: Pictures with locations
    if (form.pictures && form.pictures.length > 0) {
      const picturesWithLocation = form.pictures.filter(pic => pic.latitude && pic.longitude);
      if (picturesWithLocation.length > 0) {
        return this.calculatePicturesCenterPoint(picturesWithLocation);
      }
    }

    return null;
  }

  private getProjectAlignmentGeometry(report: any = null): any {
    return this.projectDetails?.alignment_geometry
      || this.projectDetails?.alignmentGeometry
      || report?.project?.alignment_geometry
      || report?.project?.alignmentGeometry;
  }

  private refreshLocationDerivedDataAfterAlignmentLoad(): void {
    if (!this.dailyReports?.length || !this.getProjectAlignmentGeometry()) {
      return;
    }

    const dateKey = moment(this.selectedDate).format('YYYY-MM-DD');
    const alignmentGeometry = this.getProjectAlignmentGeometry();
    const refreshKey = `${dateKey}:${typeof alignmentGeometry === 'string' ? alignmentGeometry.length : JSON.stringify(alignmentGeometry).length}`;
    if (this.lastAlignmentRefreshKey === refreshKey) {
      return;
    }
    this.lastAlignmentRefreshKey = refreshKey;

    this.buildFilteredSites();
    this.fetchReportGeometries();
    this.fetchBidItemMarkers();
    this.fetchActivityMarkers();
    this.fetchDeliveryMarkers();
    this.fetchFormMarkers();
  }

  private getStationRangeLocation(item: any, report: any = null): { latitude: number, longitude: number } | null {
    const stationRange = item?.station_range || item?.stationRange;
    if (!stationRange) return null;
    return this.stationRangeMapGeometry.getStationRangeLocation(
      stationRange,
      this.getProjectAlignmentGeometry(report)
    );
  }

  private calculateGeometryCenterPoint(geometries: any[]): { latitude: number, longitude: number } | null {
    let totalLat = 0;
    let totalLon = 0;
    let count = 0;

    const extractCoordinates = (coords: any): void => {
      if (!Array.isArray(coords)) {
        return;
      }

      if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
        totalLon += coords[0];
        totalLat += coords[1];
        count++;
        return;
      }

      coords.forEach(item => {
        if (Array.isArray(item)) {
          extractCoordinates(item);
        }
      });
    };

    geometries.forEach(coords => {
      extractCoordinates(coords);
    });

    if (count === 0) {
      return null;
    }

    return {
      latitude: totalLat / count,
      longitude: totalLon / count
    };
  }

  private calculatePicturesCenterPoint(pictures: any[]): { latitude: number, longitude: number } | null {
    // Filter to only pictures with valid coordinates (outliers already have null coords from sanitization)
    const validPictures = pictures.filter(pic => pic.latitude && pic.longitude);
    if (validPictures.length === 0) {
      return null;
    }

    const totalLat = validPictures.reduce((sum, pic) => sum + parseFloat(pic.latitude), 0);
    const totalLon = validPictures.reduce((sum, pic) => sum + parseFloat(pic.longitude), 0);

    return {
      latitude: totalLat / validPictures.length,
      longitude: totalLon / validPictures.length
    };
  }

  private transformSourceFieldsToFieldValues(lineItem: any): any {
    if (!lineItem.sources || lineItem.sources.length === 0) {
      return lineItem;
    }

    const bidItemFields = lineItem.bid_item?.fields || [];
    const transformedSources = lineItem.sources.map(source => {
      const fieldValues = {};

      bidItemFields.forEach(field => {
        fieldValues[field.id] = null;

        source.source_fields?.forEach(sourceField => {
          for (const key in sourceField) {
            if (field.name === key) {
              fieldValues[field.id] = sourceField[key];
            }
          }
        });
      });

      return {
        ...source,
        field_values: fieldValues
      };
    });

    return {
      ...lineItem,
      sources: transformedSources
    };
  }

  private resetLayerSettings(): void {
    // Reset to default layer settings for single day mode (all layers enabled)
    this.layerSettings = getLayerDefaults('singleDay');

    this.selectedTypeFilters = ['bidItem', 'activities', 'delivery', 'forms'];

    // Reset View By to default (inspector for single day mode)
    this.selectedViewBy = 'inspector';

    // Clear sticky selections
    this.selectedSiteIdsForHighlight = new Set();
    this.selectedBidItemIdsForHighlight = new Set();
    this.selectedInspectorIdsForHighlight = new Set();
    this.selectedDatesForHighlight = new Set();

    // Re-process report data to repopulate geometries and markers with reset settings
    // This ensures all map data is preserved when exiting fullscreen
    if (this.dailyReports.length > 0) {
      this.processReportData();
    }
  }

  /**
   * Handle click on an as-built geometry on the map.
   * Looks up the full report bid item data and triggers lightweight detail view.
   */
  onGeometryClick(event: { report_bid_item_id: string; bid_item_id: string; gps_feature_index: number | null; siteId: string; reportId: string }): void {
    console.log('🟢 onGeometryClick called with:', event);
    if (!event.report_bid_item_id) {
      console.log('🔴 No report_bid_item_id, returning early');
      return;
    }

    // Find the report bid item in dailyReports
    let reportBidItem: any = null;
    let report: any = null;

    for (const r of this.dailyReports) {
      if (r.bid_items) {
        const found = r.bid_items.find((bi: any) => bi.id === event.report_bid_item_id);
        if (found) {
          reportBidItem = found;
          report = r;
          break;
        }
      }
    }

    console.log('🟡 Found reportBidItem:', !!reportBidItem, 'from report:', !!report);
    if (!reportBidItem) {
      console.log('🔴 No matching reportBidItem found in dailyReports');
      return;
    }

    // Build marker-like data structure matching what showBidItemDetail expects
    const markerData = {
      ...reportBidItem,
      bid_item: reportBidItem.bid_item,
      siteName: this.siteDetails?.name || '',
      siteId: event.siteId || report?.site_id,
      reportId: event.reportId || report?.id,
      reportDate: report?.date || report?.report_date,
      projectId: this.projectId,
      allLineItems: this.getAllLineItemsForBidItem(reportBidItem.bid_item?.id, event.siteId),
    };

    this.mapService.showBidItemDetail.next({
      type: 'bid_items',
      data: markerData,
      projectId: this.projectId,
      siteId: event.siteId,
      reportId: event.reportId || report?.id,
      highlightFeatureIndex: event.gps_feature_index,
      highlightLineItemId: event.report_bid_item_id,
    });
  }

  /**
   * Get all report_bid_items (line items) for a given bid_item across all reports for a site.
   */
  private getAllLineItemsForBidItem(bidItemId: string, siteId: string): any[] {
    const lineItems: any[] = [];
    this.dailyReports.forEach(report => {
      const reportSiteId = report.site_id || report.site?.id;
      if (reportSiteId !== siteId) return;
      if (report.bid_items) {
        report.bid_items.forEach((bi: any) => {
          if (bi.bid_item?.id === bidItemId) {
            lineItems.push(bi);
          }
        });
      }
    });
    return lineItems;
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
